import { AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { DispatchedSeverity } from '@/types/api';
import { cn } from '@/lib/cn';

/**
 * Severity is never communicated by color alone — each level carries a distinct
 * icon shape and its own word. Colour-blind readers and greyscale printouts of a
 * crash record both have to stay readable.
 */
const styles: Record<DispatchedSeverity, { label: string; icon: LucideIcon; className: string }> = {
  minor: {
    label: 'Minor',
    icon: Info,
    className: 'bg-sev-minor-soft text-sev-minor border-sev-minor/20',
  },
  major: {
    label: 'Major',
    icon: AlertTriangle,
    className: 'bg-sev-major-soft text-sev-major border-sev-major/20',
  },
  critical: {
    label: 'Critical',
    icon: AlertOctagon,
    className: 'bg-sev-critical-soft text-sev-critical border-sev-critical/25',
  },
};

interface SeverityBadgeProps {
  severity: DispatchedSeverity;
  size?: 'sm' | 'md';
}

export function SeverityBadge({ severity, size = 'sm' }: SeverityBadgeProps) {
  const style = styles[severity];
  const Icon = style.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[12px]' : 'px-2.5 py-1 text-[13px]',
        style.className
      )}
    >
      <Icon className={size === 'sm' ? 'size-3' : 'size-3.5'} aria-hidden />
      {style.label}
    </span>
  );
}
